/**
 * ToolUseItem Component
 *
 * Displays a tool use event in the reasoning entity.
 * Shows tool name, input parameters, and result/error.
 */

import { AlertCircle, CheckCircle } from '@untitledui/icons';
import React from 'react';

import { decodeHtmlEntities } from '@/components/proposals/shared';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { ManifestValueLabel } from '@/lib/crm/components/properties/labels/ManifestValueLabel';

import { getToolConfig, type ToolResult } from './design-tokens';
import { TimelineConnectorLine } from './TimelineConnectorLine';
import { getToolIcon } from './toolIcons';
import { isToolRunning } from './toolRunning';
import { RunningIndicator } from './toolStatus';

interface ToolUseItemProps {
  toolResult: ToolResult;
  isFirst?: boolean;
  isLast?: boolean;
}

// ============================================================================
// Generic Adapters
// ============================================================================

/**
 * Format a single value for display
 */
function formatValue(value: unknown, maxLength = 50): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.length > maxLength
      ? `${value.substring(0, maxLength)}…`
      : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    return `${keys.length} field${keys.length === 1 ? '' : 's'}`;
  }
  return String(value).substring(0, maxLength);
}

/**
 * Format field name from snake_case to Title Case
 */
function formatFieldName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Generic input renderer - shows key fields in a clean format
 */
function renderGenericInput(input: Record<string, unknown>): React.ReactNode {
  const entries = Object.entries(input).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );

  if (entries.length === 0) {
    return <span className='text-fg-quaternary italic'>No parameters</span>;
  }

  // Show up to 5 fields
  const displayEntries = entries.slice(0, 5);

  return (
    <div className='space-y-1'>
      {displayEntries.map(([key, value]) => (
        <div key={key} className='flex gap-2'>
          <span className='text-fg-tertiary shrink-0'>
            {formatFieldName(key)}:
          </span>
          <span className='text-fg-primary'>{formatValue(value)}</span>
        </div>
      ))}
      {entries.length > 5 && (
        <span className='text-fg-quaternary italic'>
          +{entries.length - 5} more
        </span>
      )}
    </div>
  );
}

/**
 * Generic result renderer
 */
function renderGenericResult(result: unknown): React.ReactNode {
  if (result === null || result === undefined) {
    return <span className='text-fg-quaternary italic'>No result</span>;
  }

  if (typeof result === 'string') {
    return (
      <span className='text-utility-success-700 dark:text-utility-success-300 whitespace-pre-wrap break-words'>
        {formatValue(result, 4000)}
      </span>
    );
  }

  if (typeof result === 'number' || typeof result === 'boolean') {
    return (
      <span className='text-utility-success-700 dark:text-utility-success-300'>
        {String(result)}
      </span>
    );
  }

  if (Array.isArray(result)) {
    return (
      <span className='text-utility-success-700 dark:text-utility-success-300'>
        {result.length} result{result.length === 1 ? '' : 's'}
      </span>
    );
  }

  if (typeof result === 'object') {
    const obj = result as Record<string, unknown>;

    // Check for common patterns
    if ('success' in obj) {
      const message = 'message' in obj ? String(obj.message) : null;
      return (
        <span
          className={
            obj.success
              ? 'text-utility-success-700 dark:text-utility-success-300'
              : 'text-utility-error-700 dark:text-utility-error-300'
          }
        >
          {obj.success ? 'Success' : 'Failed'}
          {message && `: ${formatValue(message, 60)}`}
        </span>
      );
    }

    if ('count' in obj && typeof obj.count === 'number') {
      return (
        <span className='text-utility-success-700 dark:text-utility-success-300'>
          {obj.count} item{obj.count === 1 ? '' : 's'} found
        </span>
      );
    }

    if ('artifact_id' in obj) {
      const title = obj.artifact_title || obj.title || 'Artifact';
      return (
        <span className='text-utility-success-700 dark:text-utility-success-300'>
          Created: {formatValue(title)}
        </span>
      );
    }

    // Generic object display. Only used in the EXPANDED result panel (the
    // collapsed row uses formatResultSummary), so show every field with a
    // generous per-value cap and proper wrapping instead of clipping at 4.
    const entries = Object.entries(obj).filter(
      ([, v]) => v !== null && v !== undefined
    );

    return (
      <div className='space-y-1'>
        {entries.map(([key, value]) => (
          <div key={key} className='flex gap-2'>
            <span className='text-utility-success-600 dark:text-utility-success-400 shrink-0'>
              {formatFieldName(key)}:
            </span>
            <span className='text-utility-success-700 dark:text-utility-success-300 break-words whitespace-pre-wrap'>
              {formatValue(value, 4000)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <span className='text-utility-success-700 dark:text-utility-success-300 whitespace-pre-wrap break-words'>
      {formatValue(result, 4000)}
    </span>
  );
}

// ============================================================================
// Specific Adapters
// ============================================================================

/**
 * Web search input adapter
 */
function renderWebSearchInput(input: Record<string, unknown>): React.ReactNode {
  const query = input.query || input.search_query || input.q;
  if (!query) {
    return renderGenericInput(input);
  }

  return (
    <div className='flex gap-2'>
      <span className='text-fg-tertiary shrink-0'>Query:</span>
      <span className='text-fg-primary'>
        &quot;{formatValue(query, 80)}&quot;
      </span>
    </div>
  );
}

/**
 * Web search result adapter
 */
function renderWebSearchResult(result: unknown): React.ReactNode {
  if (!result || typeof result !== 'object') {
    return renderGenericResult(result);
  }

  const obj = result as Record<string, unknown>;
  const sources = obj.sources || obj.results || obj.items;

  if (Array.isArray(sources)) {
    return (
      <div className='space-y-1'>
        <span className='text-utility-success-700 dark:text-utility-success-300'>
          {sources.length} source{sources.length === 1 ? '' : 's'} found
        </span>
        {sources.slice(0, 3).map((source, i) => {
          const title =
            (source as Record<string, unknown>)?.title ||
            (source as Record<string, unknown>)?.name ||
            (source as Record<string, unknown>)?.url;
          return title ? (
            <div
              // eslint-disable-next-line react/no-array-index-key -- Source items lack stable identifiers; fields are cast from unknown
              key={i}
              className='text-utility-success-600 dark:text-utility-success-400 text-xs pl-2'
            >
              • {formatValue(title, 60)}
            </div>
          ) : null;
        })}
        {sources.length > 3 && (
          <span className='text-utility-success-600/70 dark:text-utility-success-400/70 text-xs pl-2'>
            +{sources.length - 3} more
          </span>
        )}
      </div>
    );
  }

  return renderGenericResult(result);
}

/**
 * CRM search input adapter
 */
function renderCrmSearchInput(input: Record<string, unknown>): React.ReactNode {
  const query = input.query || input.search || input.name || input.filter;

  if (query) {
    return (
      <div className='flex gap-2'>
        <span className='text-fg-tertiary shrink-0'>Search:</span>
        <span className='text-fg-primary'>
          &quot;{formatValue(query, 60)}&quot;
        </span>
      </div>
    );
  }

  // Show filters if present
  const filters = Object.entries(input).filter(
    ([key, value]) =>
      value !== null &&
      value !== undefined &&
      !['limit', 'offset', 'page', 'page_size'].includes(key)
  );

  if (filters.length === 0) {
    return <span className='text-fg-quaternary italic'>All records</span>;
  }

  return (
    <div className='space-y-1'>
      {filters.slice(0, 3).map(([key, value]) => (
        <div key={key} className='flex gap-2'>
          <span className='text-fg-tertiary shrink-0'>
            {formatFieldName(key)}:
          </span>
          <span className='text-fg-primary'>{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * CRM search result adapter
 */
function renderCrmSearchResult(result: unknown): React.ReactNode {
  if (!result || typeof result !== 'object') {
    return renderGenericResult(result);
  }

  const obj = result as Record<string, unknown>;

  // Handle nested types structure: {types: {entity_type: {results: [...], total_count: N}}}
  let items: unknown[] | unknown = [];
  let total = 0;

  if (obj.types && typeof obj.types === 'object') {
    const types = obj.types as Record<string, unknown>;
    const allResults: unknown[] = [];
    let totalCount = 0;

    for (const entityType of Object.values(types)) {
      if (entityType && typeof entityType === 'object') {
        const typeObj = entityType as Record<string, unknown>;
        if (Array.isArray(typeObj.results)) {
          allResults.push(...typeObj.results);
        }
        if (typeof typeObj.total_count === 'number') {
          totalCount += typeObj.total_count;
        }
      }
    }

    items = allResults;
    total = totalCount;
  } else {
    // Fall back to existing flat structure for backward compatibility
    items =
      obj.results || obj.companies || obj.contacts || obj.deals || obj.items;
  }

  if (Array.isArray(items)) {
    const count = items.length;
    // Use calculated total from nested structure, or fall back to existing logic
    if (total === 0) {
      if (typeof obj.total === 'number') {
        ({ total } = obj as { total: number });
      } else if (typeof obj.count === 'number') {
        ({ count: total } = obj as { count: number });
      } else {
        total = count;
      }
    }

    return (
      <div className='space-y-1'>
        <span className='text-utility-success-700 dark:text-utility-success-300'>
          {total} result{total === 1 ? '' : 's'} found
        </span>
        {items.slice(0, 3).map((item, i) => {
          const record = item as Record<string, unknown>;
          const name =
            record.name || record.title || record.email || record.company_name;
          const detailRaw = record.domain || record.email || record.stage;
          const detail = detailRaw ? String(detailRaw).substring(0, 20) : null;
          return name ? (
            <div
              // eslint-disable-next-line react/no-array-index-key -- Result items lack stable identifiers; fields are cast from unknown
              key={i}
              className='text-utility-success-600 dark:text-utility-success-400 text-xs pl-2'
            >
              • {formatValue(name, 40)}
              {detail && (
                <span className='text-utility-success-500 dark:text-utility-success-500'>
                  {' '}
                  ({detail})
                </span>
              )}
            </div>
          ) : null;
        })}
        {items.length > 3 && (
          <span className='text-utility-success-600/70 dark:text-utility-success-400/70 text-xs pl-2'>
            +{items.length - 3} more
          </span>
        )}
      </div>
    );
  }

  return renderGenericResult(result);
}

// ============================================================================
// Adapter Registry
// ============================================================================

type InputAdapter = (input: Record<string, unknown>) => React.ReactNode;
type ResultAdapter = (result: unknown) => React.ReactNode;

const INPUT_ADAPTERS: Record<string, InputAdapter> = {
  research_web: renderWebSearchInput,
  search_crm: renderCrmSearchInput,
};

/**
 * Report tool result adapter.
 *
 * `query_report` and `execute_saved_report` return their tabular result
 * alongside a `manifest` envelope produced by `build_report_manifest`
 * (apps/reasoning/tools/crm/manifests.py). When the envelope is present
 * we delegate to `ManifestValueLabel` to render the chart inline; if it's
 * missing we fall back to the generic renderer so older results / error
 * shapes still display sensibly.
 */
function renderReportToolResult(result: unknown): React.ReactNode {
  if (result && typeof result === 'object' && 'manifest' in result) {
    const { manifest } = result as { manifest: unknown };
    if (manifest && typeof manifest === 'object') {
      return <ManifestValueLabel value={manifest} fontSize='sm' />;
    }
  }
  return renderGenericResult(result);
}

const RESULT_ADAPTERS: Record<string, ResultAdapter> = {
  research_web: renderWebSearchResult,
  search_crm: renderCrmSearchResult,
  query_report: renderReportToolResult,
  execute_saved_report: renderReportToolResult,
};

// ============================================================================
// Gmail Subtext Formatters
// ============================================================================

/**
 * Parse `<gmail_search_results … total="N" …>` and return a friendly count line.
 * Falls back to `query: '<q>'` when counts can't be parsed but a query is in the
 * input, and to `null` (caller uses generic logic) when neither is available.
 */
function formatGmailSearchSummary(
  result: unknown,
  input: Record<string, unknown>
): string | null {
  if (typeof result !== 'string') {
    return null;
  }
  const query = typeof input.query === 'string' ? input.query : null;
  const totalMatch = result.match(/total="(\d+)"/);
  if (totalMatch) {
    const total = Number(totalMatch[1]);
    const noun = total === 1 ? 'email' : 'emails';
    return query
      ? `Found ${total} ${noun} matching '${query}'`
      : `Found ${total} ${noun}`;
  }
  if (query) {
    return `query: '${query}'`;
  }
  return null;
}

/**
 * Parse `<thread … message_count="N" …>` and return a friendly summary. If at
 * least one `<email direction="received">` block contains a `<from>` value,
 * append " with <sender>" using the LAST such value (messages are emitted
 * chronologically). Returns null when message_count can't be parsed.
 */
function formatGmailThreadSummary(
  result: unknown,
  _input: Record<string, unknown>
): string | null {
  if (typeof result !== 'string') {
    return null;
  }
  const countMatch = result.match(/message_count="(\d+)"/);
  if (!countMatch) {
    return null;
  }
  const count = countMatch[1];

  // Walk every <email direction="received"> block and capture its <from>.
  // Keep the last one we see; that's the latest received sender.
  const receivedPattern =
    /<email[^>]*direction="received"[^>]*>[\s\S]*?<from>([\s\S]*?)<\/from>/g;
  let sender: string | null = null;
  let match: RegExpExecArray | null;
  while ((match = receivedPattern.exec(result)) !== null) {
    sender = decodeHtmlEntities(match[1].trim());
  }
  return sender
    ? `Thread • ${count} messages with ${sender}`
    : `Thread • ${count} messages`;
}

/**
 * `gmail_read` returns either a single `<email …>…</email>` or a `<thread …>`.
 * For the single-email shape, return `From <from>: <subject>`; for thread
 * shape, reuse the thread formatter.
 */
function formatGmailReadSummary(
  result: unknown,
  input: Record<string, unknown>
): string | null {
  if (typeof result !== 'string') {
    return null;
  }
  if (result.trimStart().startsWith('<thread')) {
    return formatGmailThreadSummary(result, input);
  }
  const fromMatch = result.match(/<from>([\s\S]*?)<\/from>/);
  const subjectMatch = result.match(/<subject>([\s\S]*?)<\/subject>/);
  if (!fromMatch && !subjectMatch) {
    return null;
  }
  const from = fromMatch ? decodeHtmlEntities(fromMatch[1].trim()) : 'unknown';
  const subject = subjectMatch
    ? decodeHtmlEntities(subjectMatch[1].trim())
    : '(no subject)';
  return `From ${from}: ${subject}`;
}

type SummaryAdapter = (
  result: unknown,
  input: Record<string, unknown>
) => string | null;

const SUMMARY_ADAPTERS: Record<string, SummaryAdapter> = {
  gmail_search: formatGmailSearchSummary,
  gmail_read: formatGmailReadSummary,
  gmail_thread: formatGmailThreadSummary,
  // Outlook tools use the same XML format as Gmail tools
  outlook_search: formatGmailSearchSummary,
  outlook_read: formatGmailReadSummary,
  outlook_thread: formatGmailThreadSummary,
};

/**
 * Render tool input with adapter fallback
 */
function renderToolInput(
  toolName: string,
  input: Record<string, unknown>
): React.ReactNode {
  const adapter = INPUT_ADAPTERS[toolName];
  return adapter ? adapter(input) : renderGenericInput(input);
}

/**
 * Render tool result with adapter fallback
 */
function renderToolResult(toolName: string, result: unknown): React.ReactNode {
  const adapter = RESULT_ADAPTERS[toolName];
  return adapter ? adapter(result) : renderGenericResult(result);
}

/**
 * Format result summary for collapsed view
 */
function formatResultSummary(
  result: unknown,
  error: string | undefined,
  toolName: string,
  input: Record<string, unknown>
): string {
  if (error) {
    return `Error: ${error}`;
  }

  // Per-tool subtext adapter (null = couldn't parse, fall through).
  const adapter = SUMMARY_ADAPTERS[toolName];
  if (adapter) {
    const summary = adapter(result, input);
    if (summary !== null) {
      return summary;
    }
  }

  if (result === null || result === undefined) {
    return 'No result';
  }

  if (typeof result === 'object' && result !== null) {
    // Check for artifact result
    if ('artifact_id' in result) {
      const r = result as { artifact_title?: string; artifact_type?: string };
      return `Created: ${r.artifact_title || 'Artifact'} (${r.artifact_type || 'unknown'})`;
    }

    // Check for array result
    if (Array.isArray(result)) {
      return `${result.length} result${result.length === 1 ? '' : 's'}`;
    }

    // Check for common result patterns
    if ('success' in result) {
      return (result as { success: boolean }).success ? 'Success' : 'Failed';
    }

    if ('count' in result) {
      return `${(result as { count: number }).count} items`;
    }

    if ('message' in result) {
      return String((result as { message: string }).message);
    }

    // Generic object
    const keys = Object.keys(result);
    return `${keys.length} field${keys.length === 1 ? '' : 's'}`;
  }

  return String(result).substring(0, 50);
}

export const ToolUseItem: React.FC<ToolUseItemProps> = ({
  toolResult,
  isFirst = false,
  isLast = false,
}) => {
  const config = getToolConfig(toolResult.tool_name);
  const hasError = !!toolResult.error;
  const isRunning = isToolRunning(toolResult);
  const padding = isFirst ? 'pt-3 pb-1' : 'py-1';

  // Status indicator: pulsing dot while running, then check/alert when resolved
  // (or when status is absent — legacy data renders as completed).
  let statusIndicator: React.ReactNode;
  if (isRunning) {
    statusIndicator = <RunningIndicator />;
  } else if (hasError) {
    statusIndicator = (
      <AlertCircle
        className='w-3.5 h-3.5 text-utility-error-500'
        strokeWidth={2}
      />
    );
  } else {
    statusIndicator = (
      <CheckCircle
        className='w-3.5 h-3.5 text-utility-success-500'
        strokeWidth={2}
      />
    );
  }

  // Expanded result panel: show the resolved result, error message, or a
  // "Running…" placeholder while the tool is in flight. Without the running
  // branch, expanded in-flight tools fall through to renderToolResult(undefined)
  // and display "No result".
  let resultLabel: string;
  let resultBgClass: string;
  let resultContent: React.ReactNode;
  if (hasError) {
    resultLabel = 'Error';
    resultBgClass = 'bg-utility-error-50 dark:bg-utility-error-950';
    resultContent = (
      <span className='text-utility-error-700 dark:text-utility-error-300 whitespace-pre-wrap break-words'>
        {toolResult.error}
      </span>
    );
  } else if (isRunning) {
    resultLabel = 'Result';
    resultBgClass = 'bg-bg-secondary';
    resultContent = <span className='italic text-fg-quaternary'>Running…</span>;
  } else {
    resultLabel = 'Result';
    resultBgClass = 'bg-utility-success-50 dark:bg-utility-success-950';
    resultContent = renderToolResult(toolResult.tool_name, toolResult.result);
  }

  return (
    <div className='relative'>
      {/* Connector line for sub-items */}
      {!isLast && (
        <TimelineConnectorLine className='left-[8px] top-[28px] bottom-[-4px]' />
      )}

      <AccordionItem className={padding}>
        {/* Whole row toggles. The trigger contents have no nested
            interactive elements (icon + label + status + chevron, all
            visual), so wrapping them in <AccordionTrigger> is safe. */}
        <AccordionTrigger className='w-full flex items-start gap-2 text-left hover:bg-bg-secondary rounded-md px-1 py-0.5 -mx-1'>
          {/* Icon - pulses while the tool is still running */}
          <div
            className={`size-[18px] flex items-center justify-center shrink-0 mt-0.5 ${
              isRunning ? 'animate-pulse' : ''
            }`}
            data-icon-type={config.icon}
          >
            {getToolIcon(config.icon, 'w-4 h-4 text-fg-primary')}
          </div>

          {/* Content */}
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-normal text-fg-secondary'>
                {config.displayName}
              </span>
              {/* Status indicator: pulsing dot while running, then check/alert. */}
              {statusIndicator}
              <AccordionChevron
                className='w-4 h-4 text-tertiary ml-auto'
                strokeWidth={1.5}
              />
            </div>
            {/* Summary line */}
            <p className='text-xs text-fg-tertiary truncate'>
              {isRunning
                ? 'Running…'
                : formatResultSummary(
                    toolResult.result,
                    toolResult.error,
                    toolResult.tool_name,
                    toolResult.tool_input
                  )}
            </p>
          </div>
        </AccordionTrigger>

        <AccordionPanel className='ml-[26px] mt-2 max-w-2xl space-y-2'>
          {/* Input parameters */}
          <div className='space-y-1'>
            <p className='text-xs font-medium uppercase tracking-wide text-fg-quaternary'>
              Input
            </p>
            <div
              tabIndex={0}
              aria-label='Tool input parameters'
              className='bg-bg-secondary rounded-md p-2.5 text-xs leading-[18px]'
            >
              {renderToolInput(toolResult.tool_name, toolResult.tool_input)}
            </div>
          </div>

          {/* Result, error, or running placeholder */}
          <div className='space-y-1'>
            <p className='text-xs font-medium uppercase tracking-wide text-fg-quaternary'>
              {resultLabel}
            </p>
            <div
              tabIndex={0}
              aria-label='Tool result'
              className={`rounded-md p-2.5 text-xs leading-[18px] ${resultBgClass}`}
            >
              {resultContent}
            </div>
          </div>
        </AccordionPanel>
      </AccordionItem>
    </div>
  );
};

export default ToolUseItem;
